import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./DashboardSidebar.css";

import BrandlyLogo from "../../assets/DashboardSidebar/BrandlyLogo.png";
import PlusIcon from "../../assets/DashboardSidebar/Plus.png";
import LinkIcon from "../../assets/DashboardSidebar/Link.png";
import ToolsIcon from "../../assets/DashboardSidebar/Tools.png";
import MyPostsIcon from "../../assets/DashboardSidebar/Posts.png";
import PlaceholderProfile from "../../assets/DashboardSidebar/PlaceholderProfilePhoto.png";
import { LogOut, X, MoreVertical, User } from "lucide-react";
import { useQuota } from "../../context/QuotaContext";
import useAuthStore from "../../stores/authStore";

const API_URL = process.env.REACT_APP_API_URL;

const DashboardSidebar = ({ userName, profilePic, isOpen, onClose = () => {} }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { quota, fetchQuota } = useQuota();
  const { clearUser } = useAuthStore();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  useEffect(() => {
    fetchQuota();
  }, [fetchQuota]);

  const menuItems = [
    { label: "Create New Post", icon: PlusIcon, path: "/dashboard" },
    { label: "My Posts", icon: MyPostsIcon, path: "/dashboard/my-posts"},
    { label: "Generate Hooks", icon: LinkIcon, path: "/dashboard/generate-linkedin-hooks" },
    { label: "Free Tools", icon: ToolsIcon, path: "/dashboard/free-tools" },
    
  ];

  const isActive = (path) => location.pathname === path;

  // Dynamic display: Posts or Hooks left
  let usageLabel = "";
  let remaining = null;

  if (location.pathname === "/dashboard") {
    const limit = quota?.plan_limits?.post ?? 0;
    const used = quota?.usage?.post ?? 0;
    remaining = limit === -1 ? "Unlimited" : Math.max(limit - used, 0);
    usageLabel = "Posts Left";
  } else if (location.pathname === "/dashboard/generate-linkedin-hooks") {
    const limit = quota?.plan_limits?.hooks ?? 0;
    const used = quota?.usage?.hooks ?? 0;
    remaining = limit === -1 ? "Unlimited" : Math.max(limit - used, 0);
    usageLabel = "Hooks Left";
  }

  // Plan Name
  const planName =
    quota?.plan_is_active && quota?.plan_name
      ? quota.plan_name.split("-")[0].replace(/^./, (c) => c.toUpperCase()) + " Plan"
      : "Free Plan";

  // Determine CTA button text
  const isProPlan = quota?.plan_name?.startsWith("pro");
  const ctaButtonText = isProPlan ? "View Plan" : "Upgrade Plan";

  // Logout handler
  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await fetch(`${API_URL}/auth/logout`, {
        method: "GET",
        credentials: "include",
      });
    } catch (err) {
      console.warn("Logout fetch error (ignored):", err);
    } finally {
      clearUser();
      window.location.href = "/";
    }
  };

  return (
    <>
      {/* Sidebar */}
      <div className={`dashboard-sidebar ${isOpen ? "open" : ""}`}>
        {/* Mobile header */}
        <div className="dashboard-mobile-header">
          <img src={BrandlyLogo} alt="Brandly Logo" className="mobile-logo" />
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {/* Sidebar Content */}
        <div className="dashboard-sidebar-content">
          {/* Desktop logo */}
          <div className="dashboard-sidebar-logo desktop-only">
            <img src={BrandlyLogo} alt="Brandly Logo" />
          </div>

          {/* Menu */}
          <div className="dashboard-sidebar-menu">
            {menuItems.map((item) => (
              <button
                key={item.label}
                className={`menu-item ${isActive(item.path) ? "active" : ""}`}
                onClick={() => {
                  navigate(item.path);
                  onClose(); // close on mobile
                }}
              >
                <img src={item.icon} alt={item.label} />
                <span>{item.label}</span>
              </button>
            ))}
          </div>

          {/* Posts / Hooks left */}
          {remaining !== null && (
            <div className="posts-left">
              <span className="posts-count">{remaining}</span>
              {usageLabel}
            </div>
          )}

          {/* CTA */}
          <div className="dashboard-sidebar-cta">
            <button
              className="dashboard-sidebar-cta-button"
              onClick={() => {
                navigate("/pricing");
                onClose();
              }}
            >
              {ctaButtonText}
            </button>
          </div>

          {/* Profile section with 3-dot menu */}
          <div className="dashboard-sidebar-profile">
            <img
              src={profilePic || PlaceholderProfile}
              alt="User Avatar"
              className="profile-avatar"
            />
            <div className="profile-info">
              <p className="profile-name">{userName}</p>
              <p className="profile-plan">{planName}</p>
            </div>

            {/* 3-dot menu trigger */}
            <button
              className="profile-menu-btn"
              onClick={() => setShowProfileMenu((prev) => !prev)}
            >
              <MoreVertical size={18} />
            </button>

            {/* Dropdown menu */}
            {showProfileMenu && (
              <div className="profile-dropdown">
                <button
                  className="dropdown-item"
                  onClick={() => {
                    navigate("profile");
                    setShowProfileMenu(false);
                    onClose();
                  }}
                >
                  <User size={14} style={{ marginRight: "6px" }} />
                  My Profile
                </button>
                {quota?.plan_name && quota.plan_name !== "free" && (
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      navigate("subscription");
                      setShowProfileMenu(false);
                      onClose();
                    }}
                  >
                    <User size={14} style={{ marginRight: "6px" }} />
                    Manage Subscription
                  </button>
                )}
                <button
                  className="dropdown-item"
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                >
                  <LogOut size={14} style={{ marginRight: "6px" }} />
                  {isLoggingOut ? "Logging out..." : "Logout"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DashboardSidebar;
